import { useSyncExternalStore } from "react";
import { WorkspacesStore, useLinkGroups, type KeyValueStorage, type WorkspaceSnapshot } from "@helm/core";
import { TEMPLATES } from "./templates";

/**
 * The window's workspace tabs (`WorkspacesStore` in `@helm/core`: pure rules, localStorage cache,
 * synced `layout/desktop` document). Only the real desktop key syncs; the dev preview keeps its
 * own local copy.
 */

export const LEGACY_LAYOUT_KEY = "helm:workspace:desktop";
export const WORKSPACES_KEY = "helm:workspaces:desktop";

export type WorkspaceStore = WorkspacesStore;
export type { WorkspaceSnapshot };

const localStore: KeyValueStorage = {
  get: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Quota / private mode: the workspace simply isn't remembered locally.
    }
  },
};

export function newWorkspaceId(): string {
  return `ws-${crypto.randomUUID().slice(0, 8)}`;
}

const stores = new Map<string, WorkspacesStore>();

/**
 * One store per window and layout key. `legacyKey` is where the single layout was saved before
 * workspaces (`helm:workspace:desktop`; the dev preview uses `helm:workspace:preview`); the
 * workspaces document lives beside it (`helm:workspaces:…`). The legacy key is left in place.
 */
export function workspaceStore(
  legacyKey: string = LEGACY_LAYOUT_KEY,
  /** Template a fresh document starts from (default: the first, "Tradovate"); dev preview `ws=`. */
  template?: string,
): WorkspacesStore {
  const derived = legacyKey.replace(/^helm:workspace:/, "helm:workspaces:");
  const storageKey = derived === legacyKey ? `${legacyKey}:workspaces` : derived;
  let s = stores.get(storageKey);
  if (!s) {
    s = new WorkspacesStore({
      storageKey,
      legacyKey,
      storage: localStore,
      sync: storageKey === WORKSPACES_KEY,
      template: TEMPLATES.find((t) => t.id === template)?.id ?? TEMPLATES[0]?.id ?? "tradovate",
      newId: newWorkspaceId,
      // Link-group symbols are saved with the workspaces and restored before the dock renders.
      linkGroups: useLinkGroups,
    });
    stores.set(storageKey, s);
  }
  return s;
}

/** Route a layout `DocChanged` (session.ts) to the synced store, if this window has one. */
export function layoutDocChanged(docId: string, version: number): void {
  if (docId !== "desktop") return;
  void stores.get(WORKSPACES_KEY)?.refresh(version);
}

export function useWorkspaces(store: WorkspacesStore): WorkspaceSnapshot {
  return useSyncExternalStore(store.subscribe, store.getSnapshot);
}
